import { createHttpClient } from "@utils/httpClient";
import type {
  ReviewsListResponse,
  PresignedUrlResponse,
  UploadInitResponse,
  ValidateResponse,
  UploadStatusResponse,
  HistoryResponse,
} from "@models/reviews";

const client = createHttpClient(import.meta.env.VITE_REVIEWS_API_URL ?? "");

export const reviewsApi = {
  /**
   * List available review CSVs (downloads) and upload history.
   */
  list(): Promise<ReviewsListResponse> {
    return client.makeAuthenticatedRequest<ReviewsListResponse>("/reviews");
  },

  /**
   * Get a presigned S3 GET URL to download a review CSV.
   */
  getDownloadUrl(key: string): Promise<PresignedUrlResponse> {
    return client.makeAuthenticatedRequest<PresignedUrlResponse>(
      `/reviews/download?key=${encodeURIComponent(key)}`
    );
  },

  /**
   * Get a presigned S3 PUT URL + upload_key to upload a corrected CSV.
   */
  getUploadUrl(kpi: string, filename: string): Promise<UploadInitResponse> {
    return client.makeAuthenticatedRequest<UploadInitResponse>("/reviews/upload", {
      method: "POST",
      body: JSON.stringify({ kpi, filename }),
    });
  },

  /**
   * Upload a file to S3 using a presigned PUT URL (no auth header needed).
   */
  async uploadToS3(presignedUrl: string, file: File): Promise<void> {
    const response = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": "text/csv" },
    });
    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
    }
  },

  /**
   * Validate an uploaded XLSX and move it to the pending queue.
   */
  validate(uploadKey: string, originalXlsxKey: string): Promise<ValidateResponse> {
    return client.makeAuthenticatedRequest<ValidateResponse>("/reviews/validate", {
      method: "POST",
      body: JSON.stringify({ upload_key: uploadKey, original_xlsx_key: originalXlsxKey }),
    });
  },

  /**
   * Cancel a pending upload before it is imported.
   */
  cancelUpload(key: string): Promise<{ cancelled: string }> {
    return client.makeAuthenticatedRequest<{ cancelled: string }>(
      `/reviews/upload?key=${encodeURIComponent(key)}`,
      { method: "DELETE" }
    );
  },

  /**
   * Get the status of a single upload.
   */
  getUploadStatus(key: string): Promise<UploadStatusResponse> {
    return client.makeAuthenticatedRequest<UploadStatusResponse>(
      `/reviews/upload-status?key=${encodeURIComponent(key)}`
    );
  },

  /**
   * Get a presigned S3 GET URL to download a previously uploaded file.
   */
  getUploadedFileUrl(key: string): Promise<PresignedUrlResponse> {
    return client.makeAuthenticatedRequest<PresignedUrlResponse>(
      `/reviews/uploaded-file?key=${encodeURIComponent(key)}`
    );
  },

  /**
   * Full audit timeline (validated / superseded / cancelled / imported / failed) for one
   * review file, from upload_history.json.
   */
  getHistory(originalXlsxKey: string): Promise<HistoryResponse> {
    return client.makeAuthenticatedRequest<HistoryResponse>(
      `/reviews/history?original_xlsx_key=${encodeURIComponent(originalXlsxKey)}`
    );
  },
};
